import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ProtectedComponent } from '../../../../domain/components/rbac/ProtectedComponent';
import { UpdateAdminUserData } from '../../../../domain/types/admin-user';
import { Role } from '../../../../domain/types/rbac';
import { User } from '../../../../domain/types/user';
import { AdminUserService } from '../../../../domain/use-cases/admin-user/admin-user-service';
import { RoleService } from '../../../../domain/use-cases/rbac/role-service';
import Button from '../../../components/button';
import { Input } from '../../../components/inputs/input';
import Spinner from '../../../components/spinner';

// Esquema de validación para edición
const editAdminUserSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastname: z.string().min(2, 'El apellido debe tener al menos 2 caracteres'),
  roles: z.array(z.string()).min(1, 'Debe seleccionar al menos un rol'),
  isEnabled: z.boolean().default(true),
});

type EditAdminUserFormData = z.infer<typeof editAdminUserSchema>;

// Componente para Input con etiqueta y error
const FormField = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''} ${disabled ? 'bg-gray-100' : ''}`}
    />
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

export function EditAdminUserPage() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const [formData, setFormData] = useState<EditAdminUserFormData>({
    name: '',
    lastname: '',
    roles: [],
    isEnabled: true,
  });

  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const adminUserService = React.useMemo(() => new AdminUserService(), []);
  const roleService = React.useMemo(() => new RoleService(), []);

  const loadData = useCallback(async () => {
    if (!userId) {
      navigate('/dashboard/admin-users');
      return;
    }

    try {
      setLoadingUser(true);
      setLoadingRoles(true);

      // Cargar usuario y roles en paralelo
      const [userData, rolesData] = await Promise.all([
        adminUserService.getAdminUserById(userId),
        roleService.getAllRoles(),
      ]);

      if (userData) {
        setUser(userData);
        setFormData({
          name: userData.name || '',
          lastname: userData.lastname || '',
          roles: userData.roles || [],
          isEnabled: userData.isEnabled !== false,
        });
      } else {
        toast.error('Usuario no encontrado', { theme: 'colored' });
        navigate('/dashboard/admin-users');
      }

      setRoles(rolesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar los datos del usuario', {
        theme: 'colored',
      });
    } finally {
      setLoadingUser(false);
      setLoadingRoles(false);
    }
  }, [userId, adminUserService, roleService, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = (
    field: keyof EditAdminUserFormData,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error del campo si existe
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRoleChange = (roleId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      roles: checked
        ? [...prev.roles, roleId]
        : prev.roles.filter((id) => id !== roleId),
    }));

    // Limpiar error de roles si existe
    if (errors.roles) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.roles;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    try {
      editAdminUserSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !userId) {
      return;
    }

    setLoading(true);

    try {
      const updateData: UpdateAdminUserData = {
        name: formData.name,
        lastname: formData.lastname,
        roles: formData.roles,
        isEnabled: formData.isEnabled,
      };

      const result = await adminUserService.updateAdminUser(userId, updateData);

      if (result.success) {
        toast.success('Usuario administrativo actualizado exitosamente', {
          theme: 'colored',
        });
        navigate('/dashboard/admin-users');
      } else {
        toast.error(`Error al actualizar usuario: ${result.message}`, {
          theme: 'colored',
        });
      }
    } catch (error) {
      console.error('Error updating admin user:', error);
      toast.error('Error al actualizar el usuario administrativo', {
        theme: 'colored',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl text-gray-900">Usuario no encontrado</h2>
        <Button
          label="Volver"
          onClick={() => navigate('/dashboard/admin-users')}
          className="mt-4"
        />
      </div>
    );
  }

  return (
    <ProtectedComponent
      resource="admin_users"
      action="update"
      fallback={
        <div className="flex justify-center items-center min-h-96">
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900">
              Acceso Denegado
            </h2>
            <p className="text-gray-600 mt-1">
              No tienes permisos para editar usuarios
            </p>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Editar Usuario Administrativo
            </h1>
            <p className="text-gray-600 mt-1">
              Modifica la información del usuario {user.email}
            </p>
          </div>
          <Button
            label="Volver"
            variant="secondary"
            onClick={() => navigate('/dashboard/admin-users')}
          />
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Información del Usuario
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Email (no editable) */}
            <FormField
              label="Email"
              type="email"
              value={user.email}
              onChange={() => {}} // No editable
              placeholder="usuario@ejemplo.com"
              disabled
            />

            {/* Información Básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Nombre"
                type="text"
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                error={errors.name}
                placeholder="Juan"
                required
              />

              <FormField
                label="Apellido"
                type="text"
                value={formData.lastname}
                onChange={(value) => handleInputChange('lastname', value)}
                error={errors.lastname}
                placeholder="Pérez"
                required
              />
            </div>

            {/* Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Roles <span className="text-red-500">*</span>{' '}
                {loadingRoles && (
                  <span className="text-gray-500">(Cargando...)</span>
                )}
              </label>
              {errors.roles && (
                <p className="text-sm text-red-600 mb-2">{errors.roles}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={
                        role.id ? formData.roles.includes(role.id) : false
                      }
                      onChange={(e) =>
                        role.id && handleRoleChange(role.id, e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {role.name}
                      </div>
                      {role.description && (
                        <div className="text-xs text-gray-500">
                          {role.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Estado */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isEnabled"
                checked={formData.isEnabled}
                onChange={(e) =>
                  handleInputChange('isEnabled', e.target.checked)
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isEnabled" className="ml-2 text-sm text-gray-700">
                Usuario activo
              </label>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                label="Cancelar"
                variant="secondary"
                onClick={() => navigate('/dashboard/admin-users')}
                disabled={loading}
              />
              <Button
                label={loading ? 'Actualizando...' : 'Actualizar Usuario'}
                type="submit"
                disabled={loading}
                isLoading={loading}
              />
            </div>
          </form>
        </div>
      </div>
    </ProtectedComponent>
  );
}
